/**
 * Store Selector - Global store selection functionality
 * Handles store drawer, selection persistence, and cart integration
 */

const STORAGE_KEY = 'bm_preferred_store';

class StoreSelector {
  constructor() {
    // console.log('StoreSelector constructor called');
    this.drawer = document.getElementById('StoreSelectorDrawer');
    this.overlay = document.getElementById('StoreSelectorOverlay');
    this.storeList = document.querySelector('[data-store-list]');
    this.searchInput = document.querySelector('.store-selector-search-input');
    this.filterCheckbox = document.querySelector('.js-filter-available-stock');
    this.stores = [];
    this.filteredStores = [];
    this.variantId = null;
    this.productTitle = '';

    // console.log('drawer:', this.drawer);
    // console.log('overlay:', this.overlay);
    // console.log('storeList:', this.storeList);

    this.init();
  }

  init() {
    this.loadStoreData();
    this.bindEvents();
    this.loadSavedStore();
    this.updateUIWithSavedStore();
  }

  loadStoreData() {
    const dataScript = document.getElementById('store-availabilities-data');
    if (dataScript) {
      try {
        const data = JSON.parse(dataScript.textContent);
        this.stores = data.availabilities || [];
        this.filteredStores = [...this.stores];
        this.variantId = data.variantId;
        this.productTitle = data.productTitle;
      } catch (e) {
        console.error('Failed to parse store data:', e);
      }
    }
  }

  bindEvents() {
    // Open drawer buttons
    document.querySelectorAll('.js-open-store-selector').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.openDrawer();
      });
    });

    // CHANGE 1: .change-store-link and #store-pickup-card are inside the HOW TO GET IT
    // product block which gets replaced via outerHTML on every variant swap. Direct
    // addEventListener would be destroyed with the old node. Use event delegation on
    // document so the handler survives re-renders and always works regardless of DOM swaps.
    document.addEventListener('click', (e) => {
      if (e.target.closest('.change-store-link') || e.target.closest('#store-pickup-card')) {
        e.preventDefault();
        this.openDrawer();
      }
    });

    // Close drawer buttons
    document.querySelectorAll('.js-close-store-selector, [data-store-drawer-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.closeDrawer());
    });

    // Bind click events to Liquid-rendered store items (server-side rendered)
    this.bindStoreItemClicks();

    // Search input
    if (this.searchInput) {
      this.searchInput.addEventListener('input', (e) => this.handleSearch(e.target.value));
    }

    // Filter checkbox
    if (this.filterCheckbox) {
      this.filterCheckbox.addEventListener('change', () => this.applyFilters());
    }

    // Escape key
    document.addEventListener('keyup', (e) => {
      if (e.key === 'Escape' && this.drawer?.classList.contains('is-open')) {
        this.closeDrawer();
      }
    });

    // Overlay click to close
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.closeDrawer());
    }

    // Listen for variant changes
    document.addEventListener('variant:changed', (e) => {
      if (e.detail?.variantId) {
        this.variantId = e.detail.variantId;
        this.fetchStoreAvailability(e.detail.variantId);
      }

      // Run drawer inventory update after variant DOM settles.
      setTimeout(() => {
        this.populateDrawerInventoryByStore();
      }, 80);
    });

    // CHANGE 2: After a variant swap, global.js re-renders product blocks via
    // outerHTML which wipes any JS-applied store name on #store-pickup-card.
    // Re-apply the saved store to the pickup card so the correct store name
    // is always shown after switching variants.
    document.addEventListener('product:variant-change', () => {
      const savedStore = this.getSavedStore();
      if (savedStore) {
        this.updateStorePickupCard(savedStore);
      }
    });

    // When pickup card updates (store selected), refresh drawer quantities as needed.
    document.addEventListener('store:pickupcard:updated', () => {
      this.populateDrawerInventoryByStore();
    });
  }

  bindStoreItemClicks() {
    // Handle clicks on Liquid-rendered store items (from store-selector-drawer.liquid)
    // console.log('bindStoreItemClicks called');
    // console.log('storeList:', this.storeList);

    if (!this.storeList) {
      console.log('storeList is null, trying to find it again');
      this.storeList = document.querySelector('[data-store-list]');
    }

    if (!this.storeList) {
      console.error('Could not find store list element');
      return;
    }

    const items = this.storeList.querySelectorAll('.store-selector-item');
    // console.log('Found store items:', items.length);

    items.forEach((item, index) => {
      // Skip if already has click handler bound
      if (item.dataset.clickBound) {
        // console.log(`Item ${index} already has click handler`);
        return;
      }
      item.dataset.clickBound = 'true';

      // console.log(`Binding click handler to item ${index}:`, item.dataset.storeName);

      item.addEventListener('click', () => {
        console.log('Store item clicked:', item.dataset.storeName);

        // Get store data from data attributes (Liquid-rendered items)
        const storeId = item.dataset.storeId;
        const storeOid = item.dataset.storeOid;
        const storeName = item.dataset.storeName;
        const storeAddress = item.querySelector('.store-selector-item__address')?.textContent?.trim() || '';
        const storePhone = item.querySelector('.store-selector-item__phone')?.textContent?.trim() || '';

        // Build store object
        const store = {
          locationId: storeId,
          storeOid: storeOid,
          locationName: storeName,
          address: storeAddress,
          phone: storePhone,
        };

        console.log('Store object:', store);
        this.selectStore(store, item);
      });
    });
  }

  openDrawer() {

    if (!this.drawer) return;

    this.drawer.classList.add('is-open');
    if (this.overlay) {
      this.overlay.classList.add('is-open');
    }
    document.body.classList.add('store-selector-drawer-open', 'drawer-open');
    document.body.style.overflow = 'hidden';

    // Only render from JS data if we have it, otherwise keep Liquid-rendered items
    if (this.stores.length > 0) {
      this.renderStoreList();
    } else {
      // Re-bind click events to Liquid-rendered items and mark selected
      this.bindStoreItemClicks();
      this.markSelectedStoreInDrawer();
    }

    // Move selected store to top of the list (works on all pages)
    this.moveSelectedStoreToTop();

    // Populate per-store inventory based on page type
    if (this.isCartPage() || this.isComparePage()) {
      this.populateDrawerInventoryForCart();
    } else {
      this.populateDrawerInventoryByStore();
    }

    // Focus on search input
    setTimeout(() => {
      this.searchInput?.focus();
    }, 100);
  }

  closeDrawer() {
    if (!this.drawer) return;

    this.drawer.classList.remove('is-open');
    if (this.overlay) {
      this.overlay.classList.remove('is-open');
    }
    document.body.classList.remove('store-selector-drawer-open', 'drawer-open');
    document.body.style.overflow = '';
  }

  handleSearch(query) {
    const searchTerm = query.toLowerCase().trim();

    // If we have JS data, filter it
    if (this.stores.length > 0) {
      this.filteredStores = this.stores.filter((store) => {
        const nameMatch = store.locationName?.toLowerCase().includes(searchTerm);
        const addressMatch = store.address?.toLowerCase().includes(searchTerm);
        return nameMatch || addressMatch;
      });
      this.applyFilters();
    } else {
      // Filter Liquid-rendered items by showing/hiding
      this.filterLiquidItems(searchTerm);
    }
  }

  filterLiquidItems(searchTerm = '') {
    if (!this.storeList) return;

    const items = this.storeList.querySelectorAll('.store-selector-item');
    let visibleCount = 0;
    const filterOnlyAvailable = this.filterCheckbox?.checked;
    const isCart = this.isCartPage();
    const isCompare = this.isComparePage();

    items.forEach((item) => {
      const searchData = (item.dataset.storeSearch || '').toLowerCase();
      const storeName = (item.dataset.storeName || '').toLowerCase();
      const matchesSearch = !searchTerm || searchData.includes(searchTerm) || storeName.includes(searchTerm);

      // Check stock availability if filter is on
      let matchesStock = true;
      if (filterOnlyAvailable) {
        if (isCart || isCompare) {
          // For cart page, check cart inventory slot data
          const cartSlot = item.querySelector('[data-cart-inventory-slot]');
          const availableCount = parseInt(cartSlot?.dataset.availableCount || '0', 10);
          matchesStock = availableCount > 0;
        } else {
          // For product page, check status pill
          const stockPill =
            item.querySelector('.store-selector-item__stock-pill') ||
            item.querySelector('.status-pill');

          const isAvailable =
            !!stockPill &&
            (stockPill.classList.contains('available') ||
              stockPill.classList.contains('in-stock') ||
              stockPill.classList.contains('large-stock'));

          matchesStock = isAvailable;
        }
      }

      if (matchesSearch && matchesStock) {
        item.style.display = '';
        visibleCount++;
      } else {
        item.style.display = 'none';
      }
    });

    // Show/hide empty state
    const emptyState = document.querySelector('.store-selector-empty');
    if (emptyState) {
      emptyState.style.display = visibleCount === 0 ? 'block' : 'none';
    }
  }

  applyFilters() {
    // If we have JS data, filter it
    if (this.stores.length > 0) {
      let results = [...this.filteredStores];

      // Filter by available stock
      if (this.filterCheckbox?.checked) {
        results = results.filter((store) => store.available);
      }

      this.renderStoreList(results);
    } else {
      // Filter Liquid-rendered items
      const searchTerm = this.searchInput?.value?.toLowerCase().trim() || '';
      this.filterLiquidItems(searchTerm);
    }
  }

  renderStoreList(stores = this.filteredStores) {
    if (!this.storeList) return;

    const savedStore = this.getSavedStore();

    if (stores.length === 0) {
      this.storeList.innerHTML = '';
      document.querySelector('.store-selector-empty')?.style.setProperty('display', 'block');
      return;
    }

    document.querySelector('.store-selector-empty')?.style.setProperty('display', 'none');

    // Sort stores to put selected store at the top
    const sortedStores = [...stores].sort((a, b) => {
      const aSelected = savedStore?.locationId === a.locationId;
      const bSelected = savedStore?.locationId === b.locationId;
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0;
    });

    this.storeList.innerHTML = sortedStores
      .map((store) => {
        const isSelected = savedStore?.locationId === store.locationId;
        const stockClass = store.available ? 'available' : 'unavailable';
        const stockText = store.available ? 'Available in store' : 'Unavailable';

        return `
        <li class="store-selector-item ${isSelected ? 'is-selected' : ''}" 
            data-location-id="${store.locationId}"
            data-store='${JSON.stringify(store)}'>
          <h3 class="store-selector-item__name">${this.escapeHtml(store.locationName)}</h3>
          <p class="store-selector-item__address">${this.escapeHtml(store.address)}</p>
          ${store.pickUpEnabled ? `<p class="store-selector-item__pickup">Click and collect available</p>` : ''}
          <div class="store-selector-item__stock">
            <span class="store-selector-item__stock-pill ${stockClass}">${store.quantity || '0'}</span>
            <span>${stockText}</span>
          </div>
        </li>
      `;
      })
      .join('');

    // Bind click events to store items
    this.storeList.querySelectorAll('.store-selector-item').forEach((item) => {
      item.addEventListener('click', () => {
        console.log('store clicked');
        const storeData = JSON.parse(item.dataset.store);
        this.selectStore(storeData);
      });
    });

    // If list re-rendered from JS data, still refresh stock view
    this.populateDrawerInventoryByStore();
  }

  selectStore(store, clickedItem = null) {
    // console.log('selectStore called with:', store);

    // Save to localStorage
    this.saveStore(store);

    // Keep runtime translations in sync so legacy inventory code can use the new store immediately
    if (window.ShopifyTranslations) {
      window.ShopifyTranslations.selectStoreID = parseInt(store.locationId || 0, 10) || 0;
      window.ShopifyTranslations.selectStoreOID = String(store.storeOid || store.locationId || '');
      window.ShopifyTranslations.selectStoreName = store.locationName || '';
    }

    // Update selected state in drawer
    this.updateSelectedStoreInDrawer(store.locationId, clickedItem);

    // Update UI across the page
    this.updateUIWithSavedStore();

    // Update PDP store pickup card
    this.updateStorePickupCard(store);

    // Close drawer
    this.closeDrawer();

    // Dispatch event for other components
    document.dispatchEvent(
      new CustomEvent('store:selected', {
        detail: { store, variantId: this.variantId },
      })
    );

    // Update cart attributes — skip on the cart page because main-cart-items.liquid's
    // store:selected handler calls ensurePickupStoreCartAttributes(true) which already
    // writes the same attributes, avoiding a duplicate /cart/update.js call.
    if (!this.isCartPage()) {
      this.updateCartAttributes(store);
    }

    console.log('Store saved to localStorage:', this.getSavedStore());
  }

  updateSelectedStoreInDrawer(locationId, clickedItem = null) {
    // Remove is-selected from all items
    this.storeList?.querySelectorAll('.store-selector-item').forEach((item) => {
      item.classList.remove('is-selected');
    });

    // Add is-selected to clicked item or find by locationId
    if (clickedItem) {
      clickedItem.classList.add('is-selected');
    } else if (locationId) {
      const targetItem = this.storeList?.querySelector(
        `[data-store-id="${locationId}"], [data-location-id="${locationId}"]`
      );
      if (targetItem) {
        targetItem.classList.add('is-selected');
      }
    }
  }

  markSelectedStoreInDrawer() {
    const savedStore = this.getSavedStore();
    if (savedStore?.locationId) {
      this.updateSelectedStoreInDrawer(savedStore.locationId);
    }
  }

  /**
   * Move the selected store to the top of the list
   */
  moveSelectedStoreToTop() {
    // Re-query storeList to ensure it's current
    const storeList = this.storeList || document.querySelector('[data-store-list]');
    if (!storeList) return;

    const savedStore = this.getSavedStore();
    if (!savedStore?.locationId) return;

    const locationId = String(savedStore.locationId);
    
    // Find selected item by multiple possible attribute names
    let selectedItem = storeList.querySelector(`[data-store-id="${locationId}"]`);
    if (!selectedItem) {
      selectedItem = storeList.querySelector(`[data-location-id="${locationId}"]`);
    }

    if (selectedItem && storeList.firstElementChild !== selectedItem) {
      // Move selected store to the beginning of the list
      storeList.insertBefore(selectedItem, storeList.firstElementChild);
      console.log('Moved selected store to top:', savedStore.locationName);
    }
  }

  saveStore(store) {
    try {
      // Existing object storage
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          locationId: store.locationId,
          storeOid: store.storeOid,
          locationName: store.locationName,
          address: store.address,
          phone: store.phone,
          timestamp: Date.now(),
        })
      );

      // Legacy keys used by theme.liquid bootstrap for inventory translations
      const ONE_DAY = 24 * 60 * 60;
      const SEVEN_DAYS = 365 * ONE_DAY;
      document.cookie = `storeID=${encodeURIComponent(store.storeOid || '')}; path=/; domain=.bouwmaat.nl; SameSite=None; Secure; max-age=${SEVEN_DAYS}`;
      document.cookie = `storeName=${encodeURIComponent(store.locationName || '')}; path=/; domain=.bouwmaat.nl; SameSite=None; Secure; max-age=${SEVEN_DAYS}`;
      
      localStorage.setItem('storeID', String(store.locationId || ''));
      localStorage.setItem('storeOID', String(store.storeOid || store.locationId || ''));
      localStorage.setItem('storeName', String(store.locationName || ''));
    } catch (e) {
      console.error('Failed to save store to localStorage:', e);
    }
  }

  getSavedStore() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      return null;
    }
  }

  loadSavedStore() {
    return this.getSavedStore();
  }

  updateUIWithSavedStore() {
    const savedStore = this.getSavedStore();
    if (!savedStore) return;

    // Update selected state in drawer
    this.updateSelectedStoreInDrawer(savedStore.locationId);

    // Update PDP store pickup card on page load
    this.updateStorePickupCard(savedStore);
  }

  /**
   * Update the #store-pickup-card on PDP with selected store info
   * @param {Object} store - Store object with locationId, locationName, address, phone
   */
  updateStorePickupCard(store) {
    const storePickupCard = document.getElementById('store-pickup-card');
    if (!storePickupCard || !store) return;

    // Update store name (card title)
    const cardTitle = storePickupCard.querySelector('.card-title');
    if (cardTitle && store.locationName) {
      cardTitle.textContent = store.locationName;
    }

    // Store the selected store ID on the card for future reference
    storePickupCard.dataset.selectedStoreId = store.locationId || '';
    storePickupCard.dataset.selectedStoreName = store.locationName || '';

    // CHANGE 3: Also keep the "Change store" link text in sync so it never
    // reverts to "Pick store" after a variant swap re-renders the HOW TO GET IT card.
    const changeStoreLink = document.querySelector('.change-store-link');
    if (changeStoreLink) {
      changeStoreLink.textContent = store.locationName ? 'Wijzig vestiging' : 'Afhalen';
    }

    // Dispatch custom event so other components can react
    document.dispatchEvent(
      new CustomEvent('store:pickupcard:updated', {
        detail: { store, card: storePickupCard },
      })
    );
  }

  getCurrentProductSku() {
    // Try multiple sources for the SKU
    
    // Source 1: Main product SKU element (visible)
    const skuElement = document.querySelector('.product-main-sku > span');
    if (skuElement) {
      const productSku = (skuElement.textContent || '').trim();
      if (/^[0-9]+$/.test(productSku)) {
        return productSku.padStart(10, '0');
      }
    }

    // Source 2: Try product form's variant data
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const variantInput = productForm.querySelector('input[name="id"]');
      if (variantInput) {
        const variantId = variantInput.value;
        // Look for variant data in page
        const variantDataScript = document.querySelector(`script[type="application/json"][data-product-variants]`);
        if (variantDataScript) {
          try {
            const variants = JSON.parse(variantDataScript.textContent);
            const variant = variants.find(v => String(v.id) === String(variantId));
            if (variant?.sku && /^[0-9]+$/.test(variant.sku)) {
              return variant.sku.padStart(10, '0');
            }
          } catch (e) { console.warn('[Store] Failed to parse variant data:', e); }
        }
      }
    }

    // Source 3: Try cart item row SKU (for cart page context)
    const cartItemSku = document.querySelector('.cart-item[data-productSku]');
    if (cartItemSku) {
      const sku = cartItemSku.dataset.productsku;
      if (sku && /^[0-9]+$/.test(sku)) {
        return sku.padStart(10, '0');
      }
    }

    // Source 4: Try finding any visible SKU on the page
    const anySkuElement = document.querySelector('[id^="ProductSku-"]');
    if (anySkuElement) {
      const productSku = (anySkuElement.textContent || '').trim();
      if (/^[0-9]+$/.test(productSku)) {
        return productSku.padStart(10, '0');
      }
    }

    return '';
  }

  async fetchStockBySku(productSku) {
    if (!productSku) return null;

    // console.log('productSku', productSku)

    try {
      const response = await fetch(`/apps/apis/stock?productNumbers=${encodeURIComponent(productSku)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) return null;
         if(response.ok) {
          const stockAvailability = await response.json();
          return stockAvailability;
        }
    } catch (e) {
      console.error('Failed to fetch stock:', e);
      return null;
    }
  }

  setDrawerInventoryState(container, quantity, lowInventoryThreshold) {
    if (!container) return;

    const count = Math.max(parseInt(quantity || 0, 10) || 0, 0);

    let pickupStockClass = 'unavailable';
    let pickupStockText =
      window.ShopifyTranslations?.pickupStoreQtyUnavailableText || 'Unavailable in store';

    if (count > 0 && count < lowInventoryThreshold) {
      pickupStockClass = 'in-stock';
      pickupStockText =
        window.ShopifyTranslations?.pickupStoreQtyAvailableText || 'Available in store';
    } else if (count >= lowInventoryThreshold) {
      pickupStockClass = 'available';
      pickupStockText =
        window.ShopifyTranslations?.pickupStoreQtyAvailableText || 'Available in store';
    }

    container.innerHTML = `<span class="status-pill ${pickupStockClass}">${count}</span> ${pickupStockText}`;
  }

  async populateDrawerInventoryByStore(retryCount = 0) {
    if (!this.storeList) return;

    const slots = this.storeList.querySelectorAll('[data-store-inventory-slot]');
    if (!slots.length) return;

    const loading = document.querySelector('.store-selector-loading');
    if (loading) loading.style.display = 'block';

    try {
      let productSku = this.getCurrentProductSku();
      
      // If SKU not found and we haven't retried yet, wait and retry
      if (!productSku && retryCount < 3) {
        if (loading) loading.style.display = 'none';
        setTimeout(() => {
          this.populateDrawerInventoryByStore(retryCount + 1);
        }, 300);
        return;
      }
      
      if (!productSku) {
        console.log('Could not find product SKU for inventory lookup');
        if (loading) loading.style.display = 'none';
        return;
      }

      const lowInventoryThreshold = parseInt(
        window.ShopifyTranslations?.lowInventoryThreshold || '5',
        10
      );

      // Reset all slots to unavailable first
      slots.forEach((slot) => {
        const container = slot.querySelector('.pickup-store-inventory');
        this.setDrawerInventoryState(container, 0, lowInventoryThreshold);
      });

      const stockAvailability = await this.fetchStockBySku(productSku);
      if (!Array.isArray(stockAvailability) || !stockAvailability.length) return;

      const productStock = stockAvailability.find(
        (p) => parseInt(p.productNumber, 10) === parseInt(productSku, 10)
      );
      if (!productStock || !Array.isArray(productStock.pickUpStock)) return;

      const qtyByStore = new Map(
        productStock.pickUpStock.map((item) => [
          String(item.storeId),
          Math.max(parseInt(item.availableQuantity || 0, 10) || 0, 0),
        ])
      );

      slots.forEach((slot) => {
        const storeId = String(slot.dataset.storeOid || slot.dataset.storeId || '');
        const container = slot.querySelector('.pickup-store-inventory');
        const qty = qtyByStore.get(storeId) || 0;
        this.setDrawerInventoryState(container, qty, lowInventoryThreshold);
      });

      // Re-apply filter to reflect newly populated states
      const searchTerm = this.searchInput?.value?.toLowerCase().trim() || '';
      this.filterLiquidItems(searchTerm);
    } catch (e) {
      console.error('Failed to populate drawer inventory:', e);
    } finally {
      if (loading) loading.style.display = 'none';
    }
  }

  /**
   * Check if current page is a cart page
   */
  isCartPage() {
    return window.Shopify?.designMode 
      ? document.body.classList.contains('template-cart')
      : window.location.pathname.includes('/cart');
  }

  /**
   * Fetch cart data from Shopify
   */
  async fetchCartData() {
    try {
      return await window.CartStore.get();
    } catch (e) {
      console.error('Failed to fetch cart data:', e);
      return null;
    }
  }

  /**
   * Get all SKUs from cart items, padded to 10 digits
   */
  getCartSkus(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];
    
    const skus = [];
    cart.items.forEach(item => {
      const sku = (item.sku || '').trim();
      if (sku && /^[0-9]+$/.test(sku)) {
        skus.push(sku.padStart(10, '0'));
      }
    });
    return [...new Set(skus)]; // Remove duplicates
  }

  /**
   * Populate drawer inventory for cart page
   * Shows "X/Y products available in store" for each store
   */
  async populateDrawerInventoryForCart() {
    const isCart = this.isCartPage();
    const isCompare = this.isComparePage();

    if (!this.storeList || (!isCart && !isCompare)) return;

    const slots = this.storeList.querySelectorAll('[data-cart-inventory-slot]');
    if (!slots.length) return;

    const loading = document.querySelector('.store-selector-loading');
    if (loading) loading.style.display = 'block';

    try {
      let skus = [];
      let totalProducts = 0;

      if (isCompare) {
        // --- Compare page: get SKUs from compare columns ---
        const columnSkuMap = this.getCompareColumnSkus();
        if (columnSkuMap.size === 0) {
          console.log('[StoreSelector] Compare page: no SKUs found in columns');
          return;
        }
        skus = [...new Set(columnSkuMap.values())];
        totalProducts = skus.length;

        if (skus.length === 0) {
          this.setCartInventoryEmpty(slots);
          return;
        }
      } else {
        // --- Cart page: get SKUs from cart data ---
        const cart = await this.fetchCartData();
        if (!cart || !cart.items || cart.items.length === 0) {
          this.setCartInventoryEmpty(slots);
          return;
        }

        skus = this.getCartSkus(cart);
        totalProducts = skus.length;

        if (skus.length === 0) {
          this.setCartInventoryEmpty(slots);
          return;
        }
      }

      console.log(`[StoreSelector] ${isCompare ? 'Compare' : 'Cart'} SKUs:`, skus);

      // Fetch stock for all SKUs in one API call
      const stockAvailability = await this.fetchStockBySku(skus.join(','));
      if (!Array.isArray(stockAvailability) || !stockAvailability.length) {
        this.setCartInventoryUnavailable(slots, totalProducts);
        return;
      }

      // Build a map: storeId -> Set of available SKUs
      const storeAvailability = new Map();

      stockAvailability.forEach(product => {
        if (!Array.isArray(product.pickUpStock)) return;
        
        product.pickUpStock.forEach(stock => {
          const storeId = String(stock.storeId);
          const qty = parseInt(stock.availableQuantity || 0, 10);
          
          if (qty > 0) {
            if (!storeAvailability.has(storeId)) {
              storeAvailability.set(storeId, new Set());
            }
            storeAvailability.get(storeId).add(String(product.productNumber));
          }
        });
      });

      console.log('[StoreSelector] storeAvailability:', Object.fromEntries(storeAvailability));

      // Update each store slot with availability count
      slots.forEach(slot => {
        const storeId = String(slot.dataset.storeOid || slot.dataset.storeId || '');
        const textEl = slot.querySelector('.cart-availability-text');
        if (!textEl) return;

        const availableSkus = storeAvailability.get(storeId);
        const availableCount = availableSkus ? availableSkus.size : 0;

        this.setCartAvailabilityText(textEl, availableCount, totalProducts);
        
        // Store availability data for filtering
        slot.dataset.availableCount = availableCount;
        slot.dataset.totalCount = totalProducts;
      });

      // Re-apply filter to reflect newly populated states
      const searchTerm = this.searchInput?.value?.toLowerCase().trim() || '';
      this.filterLiquidItems(searchTerm);

    } catch (e) {
      console.error('[StoreSelector] Failed to populate cart/compare inventory:', e);
    } finally {
      if (loading) loading.style.display = 'none';
    }
  }

  /**
   * Set cart availability text based on available/total counts
   */
  setCartAvailabilityText(textEl, availableCount, totalProducts) {
    if (!textEl) return;

    const allAvailableText = window.ShopifyTranslations?.allProductsAvailable || 'All products available in store';
    const someAvailableText = window.ShopifyTranslations?.someProductsAvailable || '{available}/{total} products available in store';
    const noneAvailableText = window.ShopifyTranslations?.noProductsAvailable || 'No products available in store';

    if (availableCount === 0) {
      textEl.textContent = noneAvailableText;
      textEl.style.color = '#E91616';
    } else if (availableCount >= totalProducts) {
      textEl.textContent = allAvailableText;
      textEl.style.color = '#237659';
    } else {
      textEl.textContent = someAvailableText
        .replace('{available}', availableCount)
        .replace('{total}', totalProducts);
      textEl.style.color = '#506C7C';
    }
  }

  /**
   * Set all cart inventory slots to empty state (no cart items)
   */
  setCartInventoryEmpty(slots) {
    slots.forEach(slot => {
      const textEl = slot.querySelector('.cart-availability-text');
      if (textEl) {
        textEl.textContent = window.ShopifyTranslations?.emptyCart || 'No items in cart';
        textEl.style.color = '#506C7C';
      }
    });
  }

  /**
   * Set all cart inventory slots to unavailable state
   */
  setCartInventoryUnavailable(slots, totalProducts) {
    slots.forEach(slot => {
      const textEl = slot.querySelector('.cart-availability-text');
      if (textEl) {
        this.setCartAvailabilityText(textEl, 0, totalProducts);
      }
    });
  }

  /**
   * Static method to get the saved store from anywhere
   */
  static getSavedStoreStatic() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      return null;
    }
  }

  async updateCartAttributes(store) {
    try {
      const _cartFetch = (window.CartQueue ? window.CartQueue.fetch : fetch);
      const response = await _cartFetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            'Preferred Store ID': store.locationId,
            'Preferred Store Name': store.locationName,
            'Preferred Store Address': store.address,
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update cart attributes');
      }

      console.log('Cart attributes updated with preferred store');
    } catch (e) {
      console.error('Failed to update cart attributes:', e);
    }
  }

  async fetchStoreAvailability(variantId) {
    if (!variantId) return;

    const loading = document.querySelector('.store-selector-loading');
    if (loading) loading.style.display = 'block';

    try {
      // Keep this call if you still plan to parse pickup-availability section in future.
      const rootUrl = window.Shopify?.routes?.root || '/';
      const url = `${rootUrl}variants/${variantId}/?section_id=pickup-availability`;
      await fetch(url);

      // Current drawer population uses stock API + current SKU.
      await this.populateDrawerInventoryByStore();
    } catch (e) {
      console.error('Failed to fetch store availability:', e);
    } finally {
      if (loading) loading.style.display = 'none';
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text || '';
    return div.innerHTML;
  }

  /**
   * Check if current page is a product compare page
   */
  isComparePage() {
    return !!document.querySelector('product-compare') || 
          document.body.classList.contains('template-compare') ||
          !!document.getElementById('ProductComparePage-' + (document.querySelector('[id^="ProductComparePage-"]')?.id?.split('-')[1] || ''));
  }

  /**
   * Get all product SKUs from compare columns.
   * Expects each compare column to have [data-compare-product-sku] or a
   * .product-main-sku / [id^="ProductSku-"] element scoped inside it.
   * Returns a Map: columnEl -> paddedSku
   */
  getCompareColumnSkus() {
    const skuMap = new Map();

    // Each product column in the compare grid
    const columns = document.querySelectorAll('.product-compare-item.product-compare-product:not(.product-compare-item-preview)');

    columns.forEach((col) => {
      let sku = '';

      // Priority 1: explicit data attribute on the column
      if (col.dataset.compareProductSku) {
        sku = col.dataset.compareProductSku.trim();
      }

      // Priority 2: [id^="ProductSku-"] inside the column
      if (!sku) {
        const skuEl = col.querySelector('[id^="ProductSku-"]');
        if (skuEl) sku = (skuEl.textContent || '').trim();
      }

      // Priority 3: .product-main-sku span inside the column
      if (!sku) {
        const skuEl = col.querySelector('.product-main-sku > span');
        if (skuEl) sku = (skuEl.textContent || '').trim();
      }

      if (sku && /^[0-9]+$/.test(sku)) {
        skuMap.set(col, sku.padStart(10, '0'));
      }
    });

    return skuMap;
  }
}

// Custom element for the drawer
if (!customElements.get('store-selector-drawer')) {
  customElements.define(
    'store-selector-drawer',
    class extends HTMLElement {
      constructor() {
        super();
      }
    }
  );
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.storeSelector = new StoreSelector();
  // Signal that the global instance is ready so other scripts (e.g. the cart
  // page) can open the drawer immediately without polling for the instance.
  document.dispatchEvent(new CustomEvent('storeselector:ready'));
});

// Also initialize on section load (for Shopify theme editor)
document.addEventListener('shopify:section:load', () => {
  window.storeSelector = new StoreSelector();
  document.dispatchEvent(new CustomEvent('storeselector:ready'));
});
